// ProjectActivityLog — a right-side slide-out panel showing one project's audit
// feed (files, proposal, questionnaire and access activity). Opened from the
// clock icon in the project header. Data comes from GET /notifications/projects/<id>/activity/.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Activity, ChevronDown } from "lucide-react";
import {
  UploadIcon,
  TrashIcon,
  RenameIcon,
  MoveIcon,
  ApprovedIcon,
  UnapprovedIcon,
  RejectedIcon,
  AssignedIcon,
  QuestionIcon,
  AccessGrantedIcon,
  AccessRemovedIcon,
  AccessChangedIcon,
  OwnershipIcon,
  DefaultIcon,
} from "./activityIcons";
import { fetchProjectActivity } from "../../services/notificationsApi";

const PAGE_LIMIT = 20;

const FILTERS = [
  { value: "all", label: "All" },
  { value: "files", label: "Files" },
  { value: "proposal", label: "Proposal" },
  { value: "questionnaire", label: "Questionnaire" },
  { value: "access", label: "Access" },
];

// activity_type -> icon + colours. `fg` = icon colour, `bg` = circle background.
// Icons live in ./activityIcons.jsx (edit the SVGs there); tweak `fg`/`bg` here.
const ICONS = {
  file_uploaded: { Icon: UploadIcon, fg: "#2563EB", bg: "#DBF2FF" },
  file_deleted: { Icon: TrashIcon, fg: "#6B7280", bg: "#A3A3A338" },
  file_renamed: { Icon: RenameIcon, fg: "#CA8A04", bg: "#EAFC7B38" },
  file_moved: { Icon: MoveIcon, fg: "#2563EB", bg: "#EAF1FF" },
  section_approved: { Icon: ApprovedIcon, fg: "#16A34A", bg: "#D5FCCF" },
  section_unapproved: { Icon: UnapprovedIcon, fg: "#6B7280", bg: "#F3F4F6" },
  section_rejected: { Icon: RejectedIcon, fg: "#DC2626", bg: "#F7E7E6" },
  section_assigned: { Icon: AssignedIcon, fg: "#A04A97", bg: "#F6E9F4" },
  question_assigned: { Icon: QuestionIcon, fg: "#A04A97", bg: "#F6E9F4" },
  // Access and ownership. Green for gaining, grey for losing, purple for a
  // change of owner - the one people scan for.
  access_granted: { Icon: AccessGrantedIcon, fg: "#16A34A", bg: "#D5FCCF" },
  access_changed: { Icon: AccessChangedIcon, fg: "#CA8A04", bg: "#EAFC7B38" },
  access_removed: { Icon: AccessRemovedIcon, fg: "#6B7280", bg: "#F3F4F6" },
  access_left: { Icon: AccessRemovedIcon, fg: "#6B7280", bg: "#F3F4F6" },
  ownership_transferred: { Icon: OwnershipIcon, fg: "#A04A97", bg: "#F6E9F4" },
};

const CATEGORY_LABEL = {
  files: "Files",
  proposal: "Proposal",
  questionnaire: "Questionnaire",
  access: "Access",
};

// Compact relative time: "19m ago", "2h ago", "Yesterday", "3 days ago", …
function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "Just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "Last week";
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} weeks ago`;
  return new Date(iso).toLocaleDateString();
}

function ActivityRow({ item, isLast }) {
  const meta = ICONS[item.activity_type] || { Icon: DefaultIcon, fg: "#71717A", bg: "#F4F4F5" };
  const { Icon } = meta;
  const chip = CATEGORY_LABEL[item.category];
  return (
    <div className="flex gap-3">
      {/* Icon + connecting rail */}
      <div className="flex flex-col items-center">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: meta.bg }}
        >
          <Icon width={17} height={17} style={{ color: meta.fg }} />
        </div>
        {!isLast && <div className="w-px flex-1 bg-[#979797] my-1" />}
      </div>
      {/* Content */}
      <div className="pb-8 min-w-0">
        <p className="text-[14px] leading-snug text-[#18101C] break-words">{item.title}</p>
        <div className=" flex items-center gap-2 flex-wrap">
          <span className="text-[12px] text-[#52525B]">
            By {item.actor_name || "Someone"}
          </span>
          <span className="text-[#979797]">•</span>
          <span className="text-[12px] text-[#52525B]">{relativeTime(item.created_at)}</span>
          {chip && (
            <span className="text-[11px] px-2 py-0.5 rounded-md bg-[#FFF2FE] text-[#BB42AE]">
              {chip}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProjectActivityLog({ open, onClose, folderId }) {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    if (!folderId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProjectActivity(folderId, { category, limit: PAGE_LIMIT });
      setItems(data.activities || []);
      setHasMore(!!data.has_more);
      setNextCursor(data.next_cursor ?? null);
    } catch {
      setError("Couldn't load activity.");
    } finally {
      setLoading(false);
    }
  }, [folderId, category]);

  const loadMore = useCallback(async () => {
    if (!folderId || loadingMore || !hasMore || nextCursor == null) return;
    setLoadingMore(true);
    try {
      const data = await fetchProjectActivity(folderId, {
        category,
        limit: PAGE_LIMIT,
        before: nextCursor,
      });
      const more = data.activities || [];
      setItems((prev) => {
        const seen = new Set(prev.map((a) => a.id));
        return [...prev, ...more.filter((a) => !seen.has(a.id))];
      });
      setHasMore(!!data.has_more);
      setNextCursor(data.next_cursor ?? null);
    } catch {
      /* keep what we have */
    } finally {
      setLoadingMore(false);
    }
  }, [folderId, category, hasMore, nextCursor, loadingMore]);

  // (Re)load whenever the panel opens or the filter changes.
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Infinite scroll: pull the next page near the bottom.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) loadMore();
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/30 z-[60]" onClick={onClose} />
      )}
      <div
        className={`fixed top-0 right-0 bottom-0 bg-white z-[61] flex flex-col shadow-[-4px_0_24px_rgba(0,0,0,0.12)] transition-transform duration-[250ms] ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ width: "440px", maxWidth: "95vw" }}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-[#0000000D] flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-[18px] font-semibold text-[#18181B] font-outfit">
              Project Activity Log
            </h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="appearance-none text-[13px] pl-3 pr-8 h-8 rounded-md border border-[#E4E4E7] bg-white text-[#3F3F46] focus:outline-none focus:border-[#C95EBE] cursor-pointer"
                >
                  {FILTERS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={15}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none"
                />
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#F4F4F5] text-[#71717A]"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <p className="mt-1 text-[13px] text-[#71717A]">
            Track file, proposal, questionnaire and access activity
          </p>
        </div>

        {/* List */}
        <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="py-16 text-center text-[13px] text-[#A1A1AA]">Loading…</div>
          ) : error ? (
            <div className="py-16 text-center text-[13px] text-[#DC2626]">{error}</div>
          ) : items.length === 0 ? (
            <div className="py-20 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-[#F4F4F5] flex items-center justify-center mb-4">
                <Activity size={26} className="text-[#A1A1AA]" />
              </div>
              <p className="text-[15px] font-medium text-[#3F3F46]">No activity yet</p>
              <p className="text-[13px] text-[#71717A] mt-1 max-w-[260px]">
                File, proposal, questionnaire and access activity for this project will show up here.
              </p>
            </div>
          ) : (
            <>
              {items.map((item, i) => (
                <ActivityRow key={item.id} item={item} isLast={i === items.length - 1} />
              ))}
              {loadingMore && (
                <div className="py-3 text-center text-[12px] text-[#A1A1AA]">Loading more…</div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
